import { DataTexture, RGBAFormat, RepeatWrapping, UnsignedByteType, LinearFilter } from 'three';

/**
 * Procedural textures, so the repository carries no binaries.
 *
 * Value noise with a handful of octaves is enough to stop the stone from
 * reading as plastic: it is used as a bump map and a roughness map, never as
 * a colour map, which keeps the palette entirely under `config/palette.js`.
 */

function hash2(x, y, seed) {
  let h = x * 374761393 + y * 668265263 + seed * 2147483647;
  h = (h ^ (h >> 13)) * 1274126177;
  return ((h ^ (h >> 16)) >>> 0) / 4294967295;
}

function smooth(t) {
  return t * t * (3 - 2 * t);
}

function valueNoise(x, y, period, seed) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = smooth(x - xi);
  const yf = smooth(y - yi);
  const w = (a, b) => ((a % b) + b) % b;
  const v00 = hash2(w(xi, period), w(yi, period), seed);
  const v10 = hash2(w(xi + 1, period), w(yi, period), seed);
  const v01 = hash2(w(xi, period), w(yi + 1, period), seed);
  const v11 = hash2(w(xi + 1, period), w(yi + 1, period), seed);
  return (v00 * (1 - xf) + v10 * xf) * (1 - yf) + (v01 * (1 - xf) + v11 * xf) * yf;
}

/**
 * Tiling fractal noise.
 * @returns {DataTexture} grey in RGB, fbm in A — bind as bumpMap or roughnessMap.
 */
export function noiseTexture({
  size = 256,
  octaves = 5,
  baseFrequency = 4,
  gain = 0.5,
  contrast = 1,
  seed = 1,
  ridged = false,
} = {}) {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let amp = 1;
      let freq = baseFrequency;
      let sum = 0;
      let norm = 0;
      for (let o = 0; o < octaves; o++) {
        let v = valueNoise((x / size) * freq, (y / size) * freq, freq, seed + o * 17);
        if (ridged) v = 1 - Math.abs(v * 2 - 1);
        sum += v * amp;
        norm += amp;
        amp *= gain;
        freq *= 2;
      }
      let v = sum / norm;
      v = Math.min(1, Math.max(0, (v - 0.5) * contrast + 0.5));
      const i = (y * size + x) * 4;
      const b = Math.round(v * 255);
      data[i] = b;
      data[i + 1] = b;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat, UnsignedByteType);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Weathered limestone: broad mottling with fine pitting on top, plus faint
 * horizontal bedding so a vertical sweep does not look extruded.
 */
export function stoneTexture(size = 512) {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      let n = 0;
      let amp = 1;
      let freq = 3;
      let norm = 0;
      for (let o = 0; o < 6; o++) {
        n += valueNoise(u * freq, v * freq, freq, 7 + o * 23) * amp;
        norm += amp;
        amp *= 0.52;
        freq *= 2.1;
      }
      n /= norm;
      // Bedding planes and the odd deep pit.
      const bed = 0.08 * Math.sin(v * Math.PI * 2 * 9 + n * 4);
      const pit = valueNoise(u * 96, v * 96, 96, 311);
      const pitted = pit > 0.86 ? -0.28 * (pit - 0.86) / 0.14 : 0;
      const value = Math.min(1, Math.max(0, n * 0.82 + 0.16 + bed + pitted));
      const i = (y * size + x) * 4;
      const b = Math.round(value * 255);
      data[i] = b;
      data[i + 1] = b;
      data[i + 2] = b;
      data[i + 3] = Math.round((1 - value) * 255);
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat, UnsignedByteType);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

/** Thin concentric growth lines, for the surface of a shell. */
export function nacreTexture(size = 256) {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const wobble = valueNoise(u * 7, v * 7, 7, 91) * 0.5;
      const lines = 0.5 + 0.5 * Math.sin((u * 150 + wobble * 9) * Math.PI);
      const fine = 0.5 + 0.5 * Math.sin((u * 620 + wobble * 30) * Math.PI);
      const value = Math.min(1, Math.max(0, 0.55 + lines * 0.3 + fine * 0.15 - 0.2));
      const i = (y * size + x) * 4;
      const b = Math.round(value * 255);
      data[i] = b;
      data[i + 1] = b;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  const tex = new DataTexture(data, size, size, RGBAFormat, UnsignedByteType);
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
